import { AttachmentStrategy } from "@redotech/redo-model/returns/attachment-strategy";
import { PRICING_TEST_TREATMENT_KEY } from "@redotech/redo-model/shopify/cart";
import {
  MERCHANDISE_OUT_OF_STOCK_CODE,
  REDO_METADATA_ATTRIBUTE_KEY,
  REDO_OPTED_IN_FROM_CART_ATTRIBUTE_KEY,
  RedoMetadata,
  StagecoachDecisionMetadata,
} from "@redotech/redo-model/storefront/cart-attributes";
import {
  defaultTermsAndConditionsBlockedMessage,
  WidgetAutocheck,
} from "@redotech/redo-model/widget";
import {
  ExtensionShopifyClient,
  ShopifyCart,
  ShopifyCartItem,
} from "@redotech/shopify-client/ajax";
import { StorefrontClient } from "../../client/storefront";
import { getInternalAppStatusEmitter } from "../../config";
import { log } from "../../monitoring/log";
import { STANDARD_CHECKOUT_BUTTON } from "../../site-config/selectors";
import { isRedoItem } from "../../utils";
import { safeCartAdd, safeUpdateCartAttribute } from "../cart";
import type { RedoCheckoutButtonClickedEvent } from "../internal-app-events";
import { shouldBlockCheckout } from "../terms-and-conditions";
import { coveragePriceCache } from "./coverage-price-cache";

type ShopPayEventParams = {
  storefrontClient: StorefrontClient;
  productVariantId: string;
  productVariantQuantity: number;
  redoProductVariantId: string;
  pricingTestTreatmentId?: string;
};

type CheckoutButtonItemsToAdd = {
  items: (Partial<ShopifyCartItem> & {
    product_id: string | number;
    variant_id: string | number;
  })[];
};

const FALLBACK_CHECKOUT_TIMEOUT_MS = 6500;

/**
 * Creates an event handler for Shop Pay button clicks on product pages.
 * When triggered, it creates a cart with both the selected product and the Redo offering,
 * then redirects to Shop Pay checkout.
 */
export function getShopPayProductOnClickEvent({
  storefrontClient,
  productVariantId,
  productVariantQuantity,
  redoProductVariantId,
  pricingTestTreatmentId,
}: ShopPayEventParams) {
  return async () => {
    try {
      const lines = [
        {
          merchandiseId: productVariantId,
          quantity: productVariantQuantity,
          attributes: [],
        },
        { merchandiseId: redoProductVariantId, quantity: 1, attributes: [] },
      ];

      const attributes = getCoverageCartAttributes({ addCoverage: true });

      if (pricingTestTreatmentId) {
        attributes.push({
          key: PRICING_TEST_TREATMENT_KEY,
          value: pricingTestTreatmentId,
        });
      }

      const cart = await storefrontClient.createCart({
        lines,
        attributes,
        discountCodes: [],
      });

      if (cart?.checkoutUrl) {
        location.href = `${cart.checkoutUrl}&payment=shop_pay&redirect_source=direct_checkout_product`;
      } else {
        redirectToStandardCheckout();
      }
    } catch (error) {
      console.error("Error creating cart for Shop Pay checkout:", error);
      redirectToStandardCheckout();
    }
  };
}

/**
 * Fallback redirect to standard checkout
 */
function redirectToStandardCheckout() {
  location.href = `${location.origin}/checkout`;
}

export type onClickProps = {
  autocheck: WidgetAutocheck;
  client: ExtensionShopifyClient;
  cart: ShopifyCart | undefined;
  itemsToAdd: CheckoutButtonItemsToAdd;
  cartParams: any;
  anyCheckoutButtonClicked: boolean;
  allowingBubblingRef: React.MutableRefObject<boolean>;
  triggerCartRefresh: () => void;
};

export async function checkAndRemoveRedoFromCart(
  client: ExtensionShopifyClient,
  cart: ShopifyCart | undefined,
) {
  if (!cart) {
    return;
  }
  for (const item of cart.items) {
    if (isRedoItem(item)) {
      try {
        await client.removeItemFromCart(item, item.quantity);
      } catch (error) {
        console.warn("Error removing Redo item from cart:", error);
      }
    }
  }
}

export function getCheckoutOnClickEvent(
  addCoverage: boolean,
  isBuyNowHandler: boolean,
  {
    autocheck,
    client,
    cart,
    itemsToAdd,
    cartParams,
    anyCheckoutButtonClicked,
    allowingBubblingRef,
    triggerCartRefresh,
  }: onClickProps,
) {
  return async (event: React.MouseEvent | React.TouchEvent) => {
    if (allowingBubblingRef.current) {
      return;
    } else {
      // prevent default logic if we're inside a form
      if (isBuyNowHandler) event.stopPropagation();
      event.preventDefault();
    }

    // handle Terms and condition
    if (
      autocheck.termsAndConditions?.blockCheckoutIfNotAccepted &&
      autocheck.termsAndConditions.termsCheckboxSelector &&
      shouldBlockCheckout(autocheck.termsAndConditions.termsCheckboxSelector)
    ) {
      alert(
        autocheck.termsAndConditions.blockedMessageText ||
          defaultTermsAndConditionsBlockedMessage,
      );
      return;
    }

    // set opted in cart attribute
    await safeUpdateCoverageCartAttribute({ client, addCoverage });

    const shouldAddProduct =
      (autocheck?.attachmentStrategy ===
        AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT ||
        autocheck?.attachmentStrategy ===
          AttachmentStrategy.ANY_WIDGET_PRODUCT) &&
      addCoverage &&
      autocheck.addProductOnOptInClick !== false;

    if (!anyCheckoutButtonClicked) {
      // this blocks ensureProduct from removing the redo variant we're adding, also signals to checkout op and toggle if on the "any widget" attachment strategy
      const internalEmitter = getInternalAppStatusEmitter();
      const clickEvent: RedoCheckoutButtonClickedEvent = {
        type: "RedoCheckoutButtonClickedEvent",
        timestamp: Date.now(),
        addCoverage,
      };
      internalEmitter.emit(clickEvent);
    }

    // if a checkout button was already clicked don't add the redo product again.
    if (shouldAddProduct && !anyCheckoutButtonClicked) {
      let isDesiredRedoAlreadyInCart = false;
      if (globalThis.redoStorefront?.type === "commercecloud") {
        ({ isDesiredRedoAlreadyInCart } =
          await checkAndRemoveAnyUndesiredRedoItemsFromCart({
            client,
            itemsToAdd,
          }));
      }
      if (!isDesiredRedoAlreadyInCart) {
        const { error } = await safeCartAdd({
          client,
          items: itemsToAdd,
          params: cartParams,
          retries: 2,
        });
        // if there's an issue with the redo product mark it, but otherwise continue to checkout.
        if (error && error.message.includes(MERCHANDISE_OUT_OF_STOCK_CODE)) {
          // set metadata saying we Received a merchandise out of stock error so Checkout Toggle won't attempt to add redo.
          await safeUpdateCoverageCartAttribute({
            client,
            addCoverage,
            merchandiseOutOfStockError: true,
          });
        }
      }
    }

    if (!addCoverage) {
      await checkAndRemoveRedoFromCart(client, cart);
    }

    if (isBuyNowHandler) {
      const currTarget = event.target as HTMLElement;
      allowingBubblingRef.current = true;
      if ((currTarget as any)?.button) {
        (currTarget as any)?.button?.dispatchEvent(new Event("click"));
      } else if (currTarget?.click) {
        currTarget?.click();
      } else {
        currTarget?.dispatchEvent(new Event("click"));
      }
      return;
    }

    // click merchants standard checkout button
    if (autocheck.clickStandardCheckoutButton?.enabled) {
      const selector =
        autocheck.clickStandardCheckoutButton.checkoutButtonSelector ||
        STANDARD_CHECKOUT_BUTTON;
      const checkoutButton = document.querySelector(selector) as
        | HTMLButtonElement
        | undefined;

      if (checkoutButton?.click) {
        checkoutButton.click();

        if (globalThis.redoStorefront?.type === "commercecloud") {
          if (typeof globalThis.redoBasketUpdate === "function") {
            globalThis.redoBasketUpdate(); // Custom function City Beach gave us to update the cart. Does a GET https://dev.citybeach.com/on/demandware.store/Sites-CityBeachAustralia-Site/en_AU/Redo-Update and then fires page events to refresh cart numbers.
            triggerCartRefresh();
          } else {
            window.location.reload();
          }
        }

        if (autocheck.autoNavigateToCheckoutAfterTimeout ?? true) {
          return await goToCheckoutAfterTimeout();
        }
        return;
      }
    }

    if (globalThis.redoStorefront?.type === "commercecloud") {
      // The only SFCC merchant we have uses checkout buttons on the checkout page & clicks the old merchant button. If the next SFCC merchant wants it pre-checkout, make a setting.
      // Just double check the checkout URLs are correct for the new SFCC merchant and maybe removed this. Or, create a setting for checkout URL.
      throw new Error(
        "Merchant checkout buttons were not clicked, and no alternate checkout functionality has been tested",
      );
    }

    // Bundler app integration
    if (isSiteUsingBundlerApp()) {
      try {
        (globalThis.bndlr as Bundler).goToCheckout();
        return await goToCheckoutAfterTimeout();
      } catch (error) {
        console.warn("Error calling bndlr.goToCheckout:", error);
      }
    }

    // send shopper directly to checkout
    goToCheckout();
  };
}

/**
 * Set opted in cart attribute and metadata
 */
async function safeUpdateCoverageCartAttribute({
  client,
  addCoverage,
  merchandiseOutOfStockError,
}: {
  client: ExtensionShopifyClient;
  addCoverage: boolean;
  merchandiseOutOfStockError?: boolean;
}) {
  const attributes = getCoverageCartAttributes({
    addCoverage,
    merchandiseOutOfStockError,
  });
  await safeUpdateCartAttribute({
    client,
    attributes: { attributes },
    retries: 1,
  });
}

export function getCoverageCartAttributes({
  addCoverage,
  merchandiseOutOfStockError,
}: {
  addCoverage: boolean;
  merchandiseOutOfStockError?: boolean;
}): { key: string; value: string }[] {
  const metadata: RedoMetadata = {
    optedTimestamp: Date.now(),
    coveragePriceCache: coveragePriceCache.getCache(),
    stagecoachDecision: getStagecoachDecisionMetadata(),
    merchandiseOutOfStockError: merchandiseOutOfStockError ?? false,
  };
  return [
    {
      key: REDO_OPTED_IN_FROM_CART_ATTRIBUTE_KEY,
      value: addCoverage ? "true" : "false",
    },
    { key: REDO_METADATA_ATTRIBUTE_KEY, value: JSON.stringify(metadata) },
  ];
}

const isSiteUsingBundlerApp = (): boolean => {
  return (
    typeof globalThis.bndlr !== "undefined" && !!globalThis.bndlr?.goToCheckout
  );
};

const goToCheckoutAfterTimeout = async () => {
  await new Promise((resolve) =>
    setTimeout(resolve, FALLBACK_CHECKOUT_TIMEOUT_MS),
  );
  goToCheckout();
};

export const goToCheckout = () => {
  location.href = location.origin + "/checkout";
};

export const getCheckoutButtonItemsToAdd = ({
  singleProductVariantId,
  singleProductId,
  hasFinalSaleCoverage,
  sellingPlanId,
}: {
  singleProductVariantId: string | number;
  singleProductId: string | number;
  hasFinalSaleCoverage: boolean;
  sellingPlanId: number | null;
}): CheckoutButtonItemsToAdd => {
  return {
    items: [
      {
        id: singleProductVariantId, // I hate this. This is `id` prop is supposed to be a cart line id based on the type, but it's used everywhere as variant id.
        product_id: singleProductId,
        variant_id: singleProductVariantId,
        quantity: 1,
        properties: hasFinalSaleCoverage
          ? { __final_sale_coverage: "true" }
          : {},
        selling_plan: sellingPlanId ?? undefined,
      },
    ],
  };
};

function getStagecoachDecisionMetadata():
  | StagecoachDecisionMetadata
  | undefined {
  const cache = coveragePriceCache.getCache();
  const latestCartHash = cache?.latestCartHash;
  if (!cache || !latestCartHash) {
    return undefined;
  }

  const packageProtection = cache.cache[latestCartHash]?.packageProtection;
  if (!packageProtection?.modelVersionId) {
    return undefined;
  }

  return {
    cartHash: latestCartHash,
    packageProtectionPrice: packageProtection.price,
    modelVersionId: packageProtection.modelVersionId,
    betaSampleIndex: packageProtection.betaSampleIndex,
    validationMode: "detect_only",
  };
}

type RemoveUndesiredRedoProductsResult = {
  isDesiredRedoAlreadyInCart: boolean;
};
export const checkAndRemoveAnyUndesiredRedoItemsFromCart = async ({
  client,
  itemsToAdd,
}: {
  client: ExtensionShopifyClient;
  itemsToAdd: CheckoutButtonItemsToAdd;
}): Promise<RemoveUndesiredRedoProductsResult> => {
  let isDesiredRedoAlreadyInCart = false;
  const cart = await client.cartGet();
  const redoLineItems = cart.items.filter((item) => isRedoItem(item));
  const numberOfRedoItems = redoLineItems.reduce(
    (acc, item) => acc + item.quantity,
    0,
  );
  if (numberOfRedoItems > 0) {
    // Check if the desired product is in the cart.
    const desiredProduct = redoLineItems.find(
      (item) => item.id === itemsToAdd.items[0]?.id,
    );
    if (desiredProduct && desiredProduct.quantity > 0) {
      log(
        `Desired product ${desiredProduct.id} found in cart with quantity ${desiredProduct.quantity}.`,
      );
      isDesiredRedoAlreadyInCart = true;
      if (desiredProduct.quantity > 1) {
        await client.removeItemFromCart(
          desiredProduct,
          desiredProduct.quantity - 1,
          undefined,
          true,
        );
        log(`Removed extra quantity of desired product ${desiredProduct.id}.`);
      }
    }
    const remainingItems = redoLineItems.filter(
      (item) => item !== desiredProduct,
    );
    for (const item of remainingItems) {
      await client.removeItemFromCart(item, item.quantity, undefined, true);
      log(
        `Removed undesired Redo products from cart: Item ID ${item.id} of quantity ${item.quantity}.`,
      );
    }
  }

  log(
    `Verified no undesired redo products in cart after checkout buttons clicked. Desired Redo product already in the cart? ${isDesiredRedoAlreadyInCart}`,
  );
  return { isDesiredRedoAlreadyInCart };
};
